// =============================================================================
// useAppStats Hook
// =============================================================================

import { useState, useCallback, useEffect } from 'react';
import { useLogCollectorClient } from './useLogCollectorClient.js';
import type { StatsSummary, TimelineResponse, TimeBucket } from '../lib/api.js';

export interface UseAppStatsOptions {
  /** App ID to fetch stats for */
  appId: string | null;
  /** Start of time range */
  from: string;
  /** End of time range */
  to: string;
  /** Time bucket size for timeline */
  bucket?: TimeBucket;
}

export interface UseAppStatsResult {
  /** Stats summary data */
  summary: StatsSummary | null;
  /** Timeline data */
  timeline: TimelineResponse | null;
  /** Whether data is loading */
  isLoading: boolean;
  /** Error message */
  error: string | null;
  /** Refetch data */
  refetch: () => Promise<void>;
}

export function useAppStats({
  appId,
  from,
  to,
  bucket,
}: UseAppStatsOptions): UseAppStatsResult {
  const { client } = useLogCollectorClient();
  const [summary, setSummary] = useState<StatsSummary | null>(null);
  const [timeline, setTimeline] = useState<TimelineResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    if (!appId || !from || !to) {
      setSummary(null);
      setTimeline(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const [summaryData, timelineData] = await Promise.all([
        client.getStatsSummary(appId, from, to),
        client.getStatsTimeline(appId, from, to, bucket),
      ]);

      setSummary(summaryData);
      setTimeline(timelineData);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch stats';
      setError(message);
      setSummary(null);
      setTimeline(null);
    } finally {
      setIsLoading(false);
    }
  }, [client, appId, from, to, bucket]);

  // Fetch stats when dependencies change
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return {
    summary,
    timeline,
    isLoading,
    error,
    refetch: fetchStats,
  };
}
